import { useCallback, useEffect, useRef, useState } from 'react'
import { campaignFlowService } from '@forwardflow/features/campaign-flow/services'
import { useCampaignFlowStore } from '@forwardflow/features/campaign-flow/store/flow.store'
import { useFlowData } from '@forwardflow/features/campaign-flow/store/flow-data.store'
import { fillVariables } from '@forwardflow/features/campaign-flow/utils/text'

/** Fills `{variables}` for the sample lead with the campaign's assistant name. */
export const useFill = () => {
   const name = useCampaignFlowStore((s) => s.draft.voice.name) || 'Erin'
   const org = useFlowData((s) => s.org)
   const lead = useFlowData((s) => s.lead)
   return useCallback(
      (text: string) => fillVariables(text, lead, org, name),
      [name, org, lead]
   )
}

/**
 * Fills the words the test phone speaks or shows: the voice's own name, and
 * no organization name (a test must not sound like a real call from the
 * company, and some accounts carry an id in their name).
 */
export const useSpeechFill = () => {
   const name = useCampaignFlowStore((s) => s.draft.voice.name) || 'Erin'
   const org = useFlowData((s) => s.org)
   const lead = useFlowData((s) => s.lead)
   return useCallback(
      (text: string) =>
         fillVariables(text, lead, { ...org, organization: '' }, name)
            .replace(/\s+(with|from|at|of|for)\s*(?=[.,!?;]|$)/gim, '')
            .replace(/\s+(with|from|at|of)\s+(again|here|calling)\b/gi, ' $2')
            .replace(/[ \t]{2,}/g, ' '),
      [name, org, lead]
   )
}

/** The chosen voice option (hue for the orb, style label). */
export const useVoiceOption = () => {
   const id = useCampaignFlowStore((s) => s.draft.voice.optionId)
   const voices = useFlowData((s) => s.voices)
   return voices.find((v) => v.id === id) ?? voices[0]
}

/**
 * Plays a voice's recorded sample from the backend (`previewVoice`), one at
 * a time; pressing play on the voice already playing stops it.
 */
export const useVoicePreview = () => {
   const [playingId, setPlayingId] = useState<string | null>(null)
   /** The voice whose sample could not be played, to say so. */
   const [failedId, setFailedId] = useState<string | null>(null)
   const audio = useRef<HTMLAudioElement | null>(null)
   const stop = useCallback(() => {
      audio.current?.pause()
      audio.current = null
      setPlayingId(null)
   }, [])
   const play = useCallback(
      async (voiceId: string, line: string) => {
         if (playingId === voiceId) return stop()
         stop()
         setFailedId(null)
         const url = await campaignFlowService
            .previewVoice(voiceId, line)
            .catch(() => null)
         if (!url) return setFailedId(voiceId)
         const a = new Audio(url)
         audio.current = a
         a.onended = () => setPlayingId((id) => (id === voiceId ? null : id))
         a.onerror = () => {
            setPlayingId((id) => (id === voiceId ? null : id))
            setFailedId(voiceId)
         }
         setPlayingId(voiceId)
         try {
            await a.play()
         } catch {
            setPlayingId(null)
            setFailedId(voiceId)
         }
      },
      [playingId, stop]
   )
   useEffect(() => stop, [stop])
   return { play, stop, playingId, failedId }
}

/**
 * Speaks a line in the browser: the test phone's rehearsal reads the
 * playbook's own words, which no recording exists for.
 */
export const useSpeech = () => {
   const [speakingKey, setSpeakingKey] = useState<string | null>(null)
   const supported =
      typeof window !== 'undefined' && 'speechSynthesis' in window
   const stop = useCallback(() => {
      if (supported) window.speechSynthesis.cancel()
      setSpeakingKey(null)
   }, [supported])
   const speak = useCallback(
      (key: string, text: string) => {
         if (!supported) return
         if (speakingKey === key) return stop()
         window.speechSynthesis.cancel()
         const u = new SpeechSynthesisUtterance(text)
         u.rate = 1
         u.onend = () => setSpeakingKey(null)
         u.onerror = () => setSpeakingKey(null)
         setSpeakingKey(key)
         window.speechSynthesis.speak(u)
      },
      [speakingKey, stop, supported]
   )
   useEffect(() => stop, [stop])
   return { speak, stop, speakingKey, supported }
}
